import { useState, useEffect, useCallback } from 'react';
import { fetchPosts } from '../services/api';
import { loadGlobal, saveGlobal } from '../services/indexedDB';

export function useBloggerApi() {
  const [posts, setPosts] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fullyCached, setFullyCached] = useState(false);
  const [lastSync, setLastSync] = useState(0);

  const CACHE_EXPIRY = 6 * 60 * 60 * 1000;

  useEffect(() => {
    const initApp = async () => {
      setLoading(true);
      const now = Date.now();
      const globalState = await loadGlobal();
      
      if (globalState.posts && globalState.posts.length > 0) {
        setPosts(globalState.posts);
        setNextPageToken(globalState.tokens?.ALL_POSTS || null);
        setLastSync(globalState.lastSync?.ALL_POSTS || 0);
        setFullyCached(!globalState.tokens?.ALL_POSTS);

        if (now - (globalState.lastSync?.ALL_POSTS || 0) > CACHE_EXPIRY) {
          await performInitialFetch(now);
        }
      } else {
        await performInitialFetch(now);
      }
      setLoading(false);
    };

    initApp();
  }, []);

  const performInitialFetch = async (timestamp) => {
    try {
      const data = await fetchPosts(null);
      setPosts(data.posts);
      setNextPageToken(data.nextPageToken);
      setFullyCached(!data.nextPageToken);
      setLastSync(timestamp);
      await saveGlobal(data.posts, data.nextPageToken, timestamp);
    } catch (error) {
      console.error("Offline or network error", error);
    }
  };

  const fetchMore = useCallback(async () => {
    if (loading || fullyCached) return;
    setLoading(true);
    
    try {
      const data = await fetchPosts(nextPageToken);
      if (data.posts.length > 0) {
        const newPosts = [...posts, ...data.posts];
        setPosts(newPosts);
        setNextPageToken(data.nextPageToken);
        setFullyCached(!data.nextPageToken);
        await saveGlobal(newPosts, data.nextPageToken, lastSync);
      } else {
        setFullyCached(true);
        await saveGlobal(posts, null, lastSync);
      }
    } catch (error) {
      console.error("Failed to load more", error);
    } finally {
      setLoading(false);
    }
  }, [loading, fullyCached, nextPageToken, posts, lastSync]);

  const syncData = useCallback(async () => {
    setLoading(true);
    await performInitialFetch(Date.now());
    setLoading(false);
  }, []);

  return { posts, loading, fullyCached, fetchMore, syncData };
}